import {
  shippingAddressLines,
  type PortalTheme,
  type AddressOnFile,
} from '@/schema/gift-portal.schema'
import type { PortalTranslations } from '../../i18n'
import { portalCssVars, PORTAL_CSS } from '../portal-styles'
import { LangToggle } from '../lang-toggle'

interface SuccessPageProps {
  error?: string
  theme: PortalTheme
  customerName?: string
  preview?: boolean
  translations: PortalTranslations
  lang: string
  allTranslations?: Record<string, PortalTranslations>
  languages?: string[]
  email?: string | null
  isPhysical?: boolean
  address?: AddressOnFile | null
}

function renderBodyWithEmail(template: string, email?: string | null) {
  const parts = template.split('{email}')
  if (parts.length === 1 || !email) return template.replace('{email}', '')
  return (
    <>
      {parts[0]}
      <strong className="portal-success-body__email">{email}</strong>
      {parts.slice(1).join('{email}')}
    </>
  )
}

export function SuccessPage({
  error,
  theme,
  customerName,
  preview,
  translations: t,
  lang,
  allTranslations,
  languages,
  email,
  isPhysical,
  address,
}: SuccessPageProps) {
  if (error) {
    return (
      <div className="portal-theme portal-page">
        <style>
          {portalCssVars(theme, { standalone: !preview }) + '\n' + PORTAL_CSS}
        </style>

        {!preview && allTranslations && languages && (
          <LangToggle
            lang={lang}
            allTranslations={allTranslations}
            languages={languages}
          />
        )}

        <div className="portal-card portal-card--center">
          <div className="portal-icon portal-icon--error">!</div>
          <h1 className="portal-title--spaced" data-i18n="success.errorTitle">
            {t.success.errorTitle}
          </h1>
          <p className="portal-text">{error}</p>
          <a
            href={
              customerName
                ? `/${customerName}/gift-portal?lang=${lang}`
                : `/gift-portal?lang=${lang}`
            }
            className="portal-btn portal-btn--inline"
            data-i18n="success.tryAgain"
          >
            {t.success.tryAgain}
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="portal-theme portal-success">
      <style>
        {portalCssVars(theme, { standalone: !preview }) + '\n' + PORTAL_CSS}
      </style>

      {!preview && allTranslations && languages && (
        <LangToggle
          lang={lang}
          allTranslations={allTranslations}
          languages={languages}
        />
      )}

      <div className="portal-success__content">
        <div className="portal-card portal-success__panel">
          {theme.logoUrl && (
            <img src={theme.logoUrl} alt="Logo" className="portal-logo" />
          )}

          <div className="portal-success__icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <div className="portal-success__text">
            <h1 className="portal-success__title" data-i18n="success.title">
              {t.success.title}
            </h1>
            <p className="portal-success__body" data-i18n="success.body">
              {renderBodyWithEmail(t.success.body, email)}
            </p>
            {isPhysical && address && (
              <div className="portal-success__address">
                <span
                  className="portal-success__address-label"
                  data-i18n="select.addressTitle"
                >
                  {t.select.addressTitle}
                </span>
                {shippingAddressLines(address).map((line) => (
                  <span key={line} className="portal-success__address-line">
                    {line}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

SuccessPage.displayName = 'SuccessPage'
